import { Box, Divider, InputBase, Paper } from "@material-ui/core"
import Delete from "@material-ui/icons/Delete"
import FilterNone from "@material-ui/icons/FilterNone"
import produce from "immer"
import type { LeafletKeyboardEvent, Map as LeafletMap } from "leaflet"
import { Point } from "leaflet"
import type { CSSProperties, ChangeEvent, DragEvent, FormEvent, ReactElement } from "react"
import { useEffect, useMemo, useState } from "react"
import { useMap } from "react-leaflet"
import { connect } from "react-redux"

import { ColorPicker } from ".."
import type {
  AppState,
  AppThunkDispatch,
  IMapDrawing,
  ITextMapDrawing,
  MapViewerActions,
  ProjectActions
} from "../../../redux"
import {
  MapViewerPanel,
  mapTextEditorFinishEdit,
  mapTextEditorSetText,
  mapViewerTogglePanel,
  projectAddDrawing,
  projectEditDrawingText,
  projectRemoveDrawing
} from "../../../redux"
import { createNewDrawingId } from "../../../utils"
import { TooltipIconButton } from "../../atoms"

import "./draw-text-editor.styles.scss"

export interface IDrawTextEditorStateProps {
  text?: ITextMapDrawing
  saveEdit: (text: ITextMapDrawing) => void
  addDrawing: (drawing: IMapDrawing) => void
  editDrawing: (drawing: IMapDrawing) => void
  updateCurrent: (drawing: ITextMapDrawing) => void
  removeDrawing: (drawing: IMapDrawing) => void
  closeEditor: () => void
}

function DrawTextEditorView(props: IDrawTextEditorStateProps): ReactElement {
  const { text, saveEdit, addDrawing, editDrawing, updateCurrent, removeDrawing, closeEditor } = props
  const [editorPositionStyle, setEditorPositionStyle] = useState<CSSProperties>({})
  const [textValue, setTextValue] = useState<string>("")
  const [isDragging, setIsDragging] = useState<boolean>(false)
  const [previousMousePosition, setPreviousMousePosition] = useState<{
    x: number
    y: number
  }>({ x: 0, y: 0 })

  const isCreating: boolean = useMemo<boolean>(() => {
    return !text?.shape.finished || false
  }, [text])

  const clampValues: { min: number; max: number } = {
    min: 5,
    max: Number.MAX_SAFE_INTEGER
  }

  const map: LeafletMap = useMap()
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    if (text && text.shape.text !== textValue) {
      setTextValue(text.shape.text)
    }

    handleMapMove()
    map.off("move", handleMapMove)
    map.on("move", handleMapMove)
  }, [text])

  useEffect(() => {
    if (isDragging) {
      disableMapPan()
    }
    if (!isDragging) {
      enableMapPan()
    }
  }, [isDragging])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    setupShortcuts()

    return removeShortcuts
  }, [text])

  function setupShortcuts(): void {
    map.on("keydown", handleKeyPress)
  }

  function removeShortcuts(): void {
    map.off("keydown", handleKeyPress)
  }

  function handleKeyPress(event: LeafletKeyboardEvent): void {
    if (!text || isCreating) return

    const actionDuplicate: boolean = event.originalEvent.ctrlKey && event.originalEvent.key === "d"
    const actionDelete: boolean = event.originalEvent.key === "Delete"

    if (actionDuplicate) {
      duplicateDrawing()
      event.originalEvent.preventDefault()
    }

    if (actionDelete) {
      deleteDrawing()
      event.originalEvent.preventDefault()
    }
  }

  function changePosition(point: Point): void {
    setEditorPositionStyle({
      position: "relative",
      left: `${point.x}px`,
      top: `${point.y}px`,
      zIndex: 1000
    })
  }

  function handleMapMove(): void {
    if (!text) return
    const point: Point = map.project(text.shape.position)
    const containerPoint = map.project(map.getBounds().getNorthWest())
    changePosition(new Point(point.x - containerPoint.x, point.y - containerPoint.y - 74, true))
  }

  function disableMapPan(): void {
    map.dragging.disable()
  }

  function enableMapPan(): void {
    map.dragging.enable()
  }

  function handleDrag(event: DragEvent<HTMLElement>): void {
    event.dataTransfer.dropEffect = "move"
    if (!text || !isDragging) return

    const x: number = event.clientX
    const y: number = event.clientY

    if (x === 0 && y === 0) return

    setPreviousMousePosition({ x, y })

    const currentPosition: Point = map.project(text.shape.position)
    const delta: Point = new Point(x - previousMousePosition.x, y - previousMousePosition.y)

    if (delta.x === 0 && delta.y === 0) return

    const newPoint: Point = currentPosition.add(delta)

    const newText: ITextMapDrawing = produce(text, (draft: ITextMapDrawing) => {
      draft.shape.position = map.unproject(newPoint)
    })
    updateCurrent(newText)
  }

  function onDragStart(event: DragEvent<HTMLElement>): void {
    event.dataTransfer.dropEffect = "move"
    setPreviousMousePosition({ x: event.clientX, y: event.clientY })
    setIsDragging(true)
  }

  function onChange(event: ChangeEvent<HTMLInputElement>): void {
    const value: string = event.target.value
    setTextValue(value)
  }

  function onFontSizeChange(event: ChangeEvent<HTMLInputElement>): void {
    if (!text) return
    let value: number = event.target.valueAsNumber
    value = Number.isNaN(value) ? 0 : value
    value = Math.min(Math.max(value, clampValues.min), clampValues.max)

    const newText: ITextMapDrawing = produce(text, (draft: ITextMapDrawing) => {
      draft.shape.fontSize = value
    })

    updateCurrent(newText)
  }

  function onColorChange(color: string): void {
    if (!text) return

    const newText: ITextMapDrawing = produce(text, (draft: ITextMapDrawing) => {
      draft.shape.color = color
    })

    updateCurrent(newText)
  }

  function duplicateDrawing(): void {
    if (!text) return

    const duplicateDrawing: ITextMapDrawing = {
      isPopupOpen: false,
      shape: text.shape,
      id: createNewDrawingId()
    }

    addDrawing(duplicateDrawing)
  }

  function deleteDrawing(): void {
    if (!text) return

    removeDrawing(text)
    setTextValue("")
    closeEditor()
  }

  function onSubmit(event: FormEvent): void {
    event.preventDefault()
    if (!text || textValue.length === 0) return
    const newText: ITextMapDrawing = produce(text, (draft: ITextMapDrawing) => {
      draft.shape.text = textValue
      draft.shape.finished = true
    })

    setTextValue("")
    saveEdit(newText)

    if (isCreating) {
      addDrawing(newText)
    } else {
      editDrawing(newText)
    }
  }

  return text !== undefined ? (
    <Box className="draw-text-editor" style={editorPositionStyle}>
      <Paper className="draw-text-toolbar">
        <Box className={`draw-text-toolbar-container ${isCreating ? "more-padding" : ""}`}>
          <InputBase type="number" value={text.shape.fontSize} onChange={onFontSizeChange} />
          <Divider orientation="vertical" />
          <ColorPicker color={text.shape.color} onChange={onColorChange} />
          {!isCreating && (
            <>
              <TooltipIconButton
                onClick={duplicateDrawing}
                tooltip="Dupliceren"
                placement="top"
                icon={<FilterNone />}
              />
              <TooltipIconButton onClick={deleteDrawing} tooltip="Verwijderen" placement="top" icon={<Delete />} />
            </>
          )}
        </Box>
      </Paper>
      <Box
        className="drag-handle"
        draggable="true"
        onMouseEnter={disableMapPan}
        onMouseLeave={enableMapPan}
        onDragStart={onDragStart}
        onDrag={handleDrag}
        onMouseUp={() => setIsDragging(false)}
      />
      <form onSubmit={onSubmit}>
        <input
          type="text"
          className="map-text-field"
          style={{
            fontSize: text.shape.fontSize,
            color: text.shape.color
          }}
          placeholder="Typ om tekst toe te voegen"
          defaultValue={textValue}
          onChange={onChange}
        />
      </form>
    </Box>
  ) : (
    <></>
  )
}

function mapStateToProps(state: AppState) {
  return {
    text: state.mapTextEditor.currentText
  }
}

function mapDispatchToProps(dispatch: AppThunkDispatch<ProjectActions | MapViewerActions>) {
  return {
    saveEdit: (text: ITextMapDrawing) => {
      dispatch(mapTextEditorSetText(text))
      dispatch(mapTextEditorFinishEdit())
      dispatch(mapViewerTogglePanel(MapViewerPanel.DrawText, false))
    },
    closeEditor: () => dispatch(mapTextEditorFinishEdit()),
    addDrawing: (drawing: IMapDrawing) => dispatch(projectAddDrawing(drawing)),
    editDrawing: (drawing: IMapDrawing) => dispatch(projectEditDrawingText(drawing)),
    updateCurrent: (drawing: ITextMapDrawing) => dispatch(mapTextEditorSetText(drawing)),
    removeDrawing: (drawing: IMapDrawing) => dispatch(projectRemoveDrawing(drawing))
  }
}

export const DrawTextEditor = connect(mapStateToProps, mapDispatchToProps)(DrawTextEditorView)
